import {
  ApplicationConfig,
  ErrorHandler,
  isDevMode,
  provideZoneChangeDetection
} from '@angular/core';
import { provideRouter } from '@angular/router';
import { appRoutes } from './app.routes';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatListModule } from '@angular/material/list';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import * as Sentry from '@sentry/angular';
import { environment } from '../environments/environment';
import { provideStates, provideStore } from '@ngxs/store';
import { withNgxsReduxDevtoolsPlugin } from '@ngxs/devtools-plugin';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { HttpInterceptor } from './core/interceptors/http.interceptor';
import { AuthState } from './core/authentication/auth.state';
import { AppState } from './app.state';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS } from '@angular/material/form-field';
import { MAT_SNACK_BAR_DEFAULT_OPTIONS } from '@angular/material/snack-bar';
import { TranslationService } from './core/services/translationService';
import { TranslocoHttpLoader } from './transloco-loader';
import { provideTransloco } from '@jsverse/transloco';
import { MAT_TOOLTIP_DEFAULT_OPTIONS } from '@angular/material/tooltip';
import { cmsMatTooltipDefaults } from './app.defaults';
import { ModuleEntityState } from './module/services/module.entity.service';
import { FeedbackEntityState } from './feedback/services/feedback.entity.service';
import { LearningUnitEntityState } from './learning-unit/services/learning-unit.entity.service';
import { TestEntityState } from './test/services/test.entity.service';
import { ConsumerEntityState } from './consumer/services/consumer.entity.service';
import { SubjectEntityState } from './subject/services/subject.entity.service';
import { SequenceEntityState } from './sequence/services/sequence.entity.service';
import { ExamEntityState } from './lesson/exam/services/exam.entity.service';
import { ExerciseEntityState } from './lesson/exercise/services/exercise.entity.service';
import { GenericTreeEntityState } from './generic-tree/services/generic-tree.entity.service';
import { GlobalMetaDataEntityState } from './global-metadata/services/global-metadata.entity.service';
import { GlobalMetaDataTypeEntityState } from './global-metadata/services/global-metadata-type.entity.service';
import { ProductEntityState } from './product/services/product.entity.service';
import { ObjectTypeEntityState } from './core/global-enums/object-type/services/object-type.entity.service';
import { SchoolClassEntityState } from './core/global-enums/school-class/services/school-class.entity.service';
import { LearningSectionEntityState } from './learning-section/services/learning-section.entity.service';
import { LearningSectionContainerEntityState } from './learning-section/learning-section-container/services/learning-section-container.entity.service';
import { LearningSectionLayoutEntityState } from './learning-section/learning-section-layout/services/learning-section-layout.entity.service';
import { MAT_DIALOG_DEFAULT_OPTIONS, MatDialogConfig } from '@angular/material/dialog';
import { FunctionalHelpEntityState } from './functionalHelp/services/functional-help.entity.service';
import { EditorEntityState } from './editor/services/editor.entity.service';
import { AssetEntityState } from './asset/services/asset.entity.service';

export const GlobalDialogConfig: MatDialogConfig = {
  // Custom CSS class to apply a blur effect to the background overlay
  backdropClass: 'frosted-glass',

  // Whether the dialog has a background backdrop (defaults to true)
  // hasBackdrop: true,

  // Focuses the dialog container for accessibility and to prevent focus from staying on the trigger button
  autoFocus: 'dialog',
};

export const appConfig: ApplicationConfig = {
  providers: [
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(appRoutes),
    provideAnimationsAsync(),
    provideHttpClient(withInterceptors([HttpInterceptor])),
    TranslationService,
    MatSidenavModule,
    MatListModule,
    MatIconModule,
    MatMenuModule,
    { provide: ErrorHandler, useValue: Sentry.createErrorHandler() },
    { provide: MAT_FORM_FIELD_DEFAULT_OPTIONS, useValue: { appearance: 'outline' } },
    { provide: MAT_SNACK_BAR_DEFAULT_OPTIONS, useValue: { duration: 4500 } },
    provideStore([
      AppState,
      AuthState], {
      developmentMode: !environment.isProd
    }, withNgxsReduxDevtoolsPlugin({ disabled: environment.isProd })),
    {
      provide: MAT_TOOLTIP_DEFAULT_OPTIONS,
      useValue: cmsMatTooltipDefaults
    },
    { provide: MAT_DIALOG_DEFAULT_OPTIONS, useValue: GlobalDialogConfig },
    provideStates([
      AssetEntityState,
      ConsumerEntityState,
      EditorEntityState,
      ExamEntityState,
      ExerciseEntityState,
      FeedbackEntityState,
      FunctionalHelpEntityState,
      GenericTreeEntityState,
      GlobalMetaDataEntityState,
      GlobalMetaDataTypeEntityState,
      LearningSectionContainerEntityState,
      LearningSectionLayoutEntityState,
      LearningSectionEntityState,
      LearningUnitEntityState,
      ModuleEntityState,
      ObjectTypeEntityState,
      ProductEntityState,
      SchoolClassEntityState,
      SequenceEntityState,
      SubjectEntityState,
      TestEntityState
    ]),
    provideHttpClient(),
    provideTransloco({
      config: {
        availableLangs: ['de', 'en'],
        defaultLang: 'de',
        // Remove this option if your application doesn't support changing language in runtime.
        reRenderOnLangChange: true,
        prodMode: !isDevMode(),
      },
      loader: TranslocoHttpLoader
    })
  ]
};
